// All user-facing strings for Twasal Awasal, in English and Arabic (Modern Standard).
// Structure mirrors page sections so components can pull what they need with t.section.key

const en = {
  meta: {
    languageName: "English",
    arName: "العربية",
  },
  nav: {
    home: "Home",
    features: "Services",
    services: "Services",
    solutions: "Business Solutions",
    track: "Track Shipment",
    pricing: "Pricing",
    about: "About Us",
    partner: "Contact",
    contact: "Contact",
    login: "Login to Dashboard",
  },
  common: {
    trackShipment: "Track Your Shipment",
    getBusinessQuote: "Get a Business Quote",
    sendParcel: "Send a Parcel",
    partnerWithUs: "Contact Us",
    seeHowItWorks: "See How It Works",
    viewPricing: "View Pricing",
    learnMore: "Learn more",
    sendEnquiry: "Send Enquiry",
    sending: "Sending...",
    sendAnother: "Send another enquiry",
    backHome: "Back to home",
    becomePartner: "Get Started",
    viewAllFeatures: "View all services",
    startConversation: "Start a conversation",
    startPilot: "Start Pilot Trial",
    poweredByDelfi: "Twasal Awasal",
  },
  hero: {
    eyebrow: "Domestic · Cargo · International · Powered by AI",
    headline1: "Every delivery,",
    headline2: "",
    highlight: "smarter.",
    subheadline:
      "Twasal Awasal is Oman's technology-driven delivery and postal network — moving parcels, cargo, and freight across Muscat, the GCC, and beyond. Every shipment is planned, tracked, and delivered using AI-driven technology, end to end.",
    ctaPrimary: "Track Your Shipment",
    ctaSecondary: "Get a Business Quote",
    trust: "Last-Mile Delivery · Courier & Cargo · International Freight · E-commerce Delivery · Enterprise Logistics",
    serviceModes: ["Domestic Last-Mile", "Cargo & Freight", "International Shipping"],
    sceneLabel: "Live Network · Muscat → World",
    activeBadge: "AI-Routed",
    zoneBadge: "Multi-Modal",
    notifTitle: "Live Network Activity",
    notifJustNow: "Just Now",
    notifEtaUpdates: "Network updates every second",
    notifications: [
      { title: "Pickup confirmed — Muttrah, Muscat", meta: "Rider assigned · En route · ETA 6 min" },
      { title: "Cargo dispatched — Sohar Port → Dubai", meta: "Truck sealed · Departed 08:42 GST" },
      { title: "International shipment in transit — Muscat → London Heathrow", meta: "Airborne · Flight time 7h 20m · On schedule" },
      { title: "Delivered — Proof of delivery captured", meta: "Photo + signature stored · 14:17 GST" },
    ],
    trackWidget: {
      title: "Where's your parcel?",
      sub: "Enter your tracking number to see live, real-time status.",
      placeholder: "Enter tracking number (e.g. TWA123456789OM)",
      button: "Track Now",
    },
    stats: [
      "Muscat-wide coverage, every district",
      "Cross-border reach across the GCC",
      "AI-optimised routing on every delivery",
      "Digital proof of delivery, every shipment",
    ],
    forYou: {
      heading: "Sending a parcel?",
      body: "Book a pickup in minutes, track it live, and get proof the moment it's delivered — whether it's crossing Muscat or crossing a border.",
      cta: "Send a Parcel",
    },
    forBusiness: {
      heading: "Shipping at volume?",
      body: "From daily e-commerce orders to enterprise freight contracts, our business plans give you dedicated support, reporting, and rates that scale with your growth.",
      cta: "Get a Business Quote",
    },
    phone: {
      greeting: "Hello, Ahmed",
      prompt: "What are you sending today?",
      actions: [
        { title: "Deliver Now", sub: "Instant pickup & delivery" },
        { title: "By End of Day", sub: "Delivery before evening" },
        { title: "Truck Delivery", sub: "For heavy & large items" },
        { title: "Schedule", sub: "Pick your date & time" },
      ],
      recentOrders: "Recent Orders",
      viewAll: "View All",
      orders: [
        { from: "Muttrah Souq", to: "Qurum Beach", amount: "6.409", status: "CONFIRMED" },
        { from: "Sultan Qaboos Mosque", to: "Muttrah Souq", amount: "8.366", status: "CONFIRMED" },
        { from: "Qurum Beach", to: "Ruwi Business", amount: "5.116", status: "CONFIRMED" },
      ],
      tabs: ["Home", "My Orders", "Support", "Profile"],
      location: "Muscat, Oman",
    },
  },
  pillars: {
    eyebrow: "Why Twasal Awasal",
    title: "Built on technology. Delivered by people.",
    description:
      "We combine a licensed Omani delivery network with AI-driven technology — so every shipment takes the shortest route, stays visible at every step, and arrives in safe hands.",
    items: [
      { title: "AI-Powered Routing", description: "Our system continuously calculates the fastest, most efficient route for every driver — factoring in live traffic, delivery windows, and vehicle load." },
      { title: "Real-Time Tracking", description: "Every shipment is visible on a live map from pickup to delivery. You always know exactly where your parcel is." },
      { title: "Digital Proof of Delivery", description: "Photo, signature, and timestamp captured on every drop-off — stored securely and available the moment you need it." },
    ],
  },
  homeFeatures: {
    eyebrow: "For You & For Business",
    title: "One network. Every kind of sender.",
    description: "From individuals sending a single parcel to businesses shipping at scale — Twasal Awasal is built for you.",
    items: [
      { title: "Sending a parcel?", description: "Book a pickup in minutes, track it live, and get proof the moment it's delivered — whether it's crossing Muscat or crossing a border." },
      { title: "Shipping at volume?", description: "From daily e-commerce orders to enterprise freight contracts, our business plans give you dedicated support, reporting, and rates that scale with your growth." },
      { title: "Live Tracking", description: "Sub-minute GPS accuracy on every active delivery. You and your customers see exactly where the parcel is — without a phone call." },
      { title: "AI Route Optimisation", description: "Smarter routes. Fewer failed deliveries. Our routing engine learns from traffic, time windows, and driver history — cutting delivery times automatically." },
      { title: "Digital Proof of Delivery", description: "Photo, signature, and timestamp captured at the door — stored securely and exportable in one click." },
      { title: "Customer Notifications", description: "Automatic SMS and WhatsApp alerts at every milestone — branded, instant, and zero effort from your team." },
    ],
  },
  homeQuote: {
    eyebrow: "Trust Quote",
    quote1: "Twasal Awasal gives our customers the live tracking and reliability they expect — and gives us the",
    quote2: "operational backbone to keep growing",
    quote3: " across Oman.",
    attribution: "— A Twasal Awasal Business Customer",
  },
  ctaBanner: {
    eyebrow: "Ready to ship",
    title: "Ready to send smarter?",
    subtitle: "Whether it's one parcel or one thousand, Twasal Awasal gets it there — tracked, verified, and on time.",
    primary: "Track Your Shipment",
    secondary: "Get a Business Quote",
    bulletsHeading: "Twasal Awasal is the right fit for you if:",
    bullets: [
      "You need to send a single parcel across Muscat — same-day or next-day",
      "You run an online store and want reliable fulfilment, COD handling, and live tracking",
      "You ship 50–600+ parcels a month and need better rates with dedicated account support",
      "You're a corporate or government partner needing audit-ready reporting on every delivery",
    ],
  },
  appDownload: {
    eyebrow: "Mobile App",
    headlineLead: "Your shipments.",
    headlineHighlight: "In your pocket.",
    body:
      "The Twasal Awasal app puts every shipment in your hand. Book a pickup in seconds. Track in real time. View proof of delivery the moment it arrives. Arabic and English. iPhone and Android.",
    appleSubtitle: "Download on the",
    appleTitle: "App Store",
    googleSubtitle: "Get it on",
    googleTitle: "Google Play",
    appTagline: "Every parcel · Every distance",
    bullets: [
      "Booking — One tap to book. Same-day or scheduled in under 30 seconds.",
      "Live Tracking — GPS tracking, sub-minute. Know before your customer asks.",
      "Proof of Delivery — Digital POD at the door. No paper, no disputes.",
      "Notifications — Alerts, automated. SMS and WhatsApp, branded, instant.",
    ],
  },
  features: {
    eyebrow: "Services",
    titleLead: "Every kind of delivery.",
    titleHighlight: "One trusted network.",
    description:
      "From a single parcel across Muscat to freight crossing borders, Twasal Awasal moves it all — with AI-driven planning behind every shipment.",
    groups: [
      {
        title: "Domestic Last-Mile & Courier",
        eyebrow: "Domestic",
        description: "Same-day and next-day delivery across Muscat and Oman's major cities. Built for everyday parcels, documents, and time-sensitive deliveries.",
        items: [
          { title: "Same-day delivery within Muscat", description: "Book before 12:00 GST for same-day handover, anywhere in Muscat governorate." },
          { title: "Next-day delivery across Oman", description: "Reliable next-day reach to Sohar, Nizwa, Salalah, and every major Omani city." },
          { title: "Live GPS tracking on every run", description: "Sub-minute GPS updates on every active shipment, available to sender and recipient." },
          { title: "Digital proof of delivery", description: "Photo, signature, and timestamp captured at the door — exportable in one click." },
        ],
      },
      {
        title: "Cargo & Freight",
        eyebrow: "Cargo",
        description: "Heavier shipments and bulk cargo moved across Oman and the wider GCC — from single pallets to full truckloads.",
        items: [
          { title: "Domestic and GCC cross-border cargo", description: "Single pallet to full truckload, moved across Oman and the wider GCC corridors." },
          { title: "Flexible vehicle options", description: "Right-sized vehicles for bulk, oversized, and special-handling loads." },
          { title: "Real-time shipment status & ETA", description: "Continuous status updates and accurate ETA windows from pickup to drop-off." },
          { title: "Dedicated coordination", description: "A named coordinator for every time-critical or high-value freight movement." },
        ],
      },
      {
        title: "International Shipping",
        eyebrow: "International",
        description: "Send documents, parcels, and freight to major destinations worldwide, with full customs documentation support handled on your behalf.",
        items: [
          { title: "International parcel & document delivery", description: "Reliable outbound shipping to major destinations across Europe, Asia, the Americas, and Africa." },
          { title: "Customs documentation & clearance", description: "Our team handles all customs paperwork — you ship, we clear." },
          { title: "End-to-end tracking", description: "One tracking number from Oman to destination — fully visible at every leg." },
          { title: "Competitive international rates", description: "Volume-negotiated rates on every major international corridor." },
        ],
      },
      {
        title: "E-commerce & Cash-on-Delivery",
        eyebrow: "E-commerce",
        description: "Built for online sellers who need reliable fulfilment and transparent cash-on-delivery handling — without managing a delivery fleet themselves.",
        items: [
          { title: "COD with real-time reconciliation", description: "Collected, tracked, and reconciled — with a full audit trail for every cash transaction." },
          { title: "Automated SMS & WhatsApp updates", description: "Branded customer notifications at every shipment milestone — zero effort from your team." },
          { title: "Returns & reverse logistics", description: "Returns handled end-to-end, including pickup, inspection, and refund coordination." },
          { title: "E-commerce platform integration", description: "Direct integration with popular e-commerce platforms — orders flow straight to dispatch." },
        ],
      },
      {
        title: "Enterprise & Postal Network Solutions",
        eyebrow: "Enterprise",
        description: "For corporates, government entities, and high-volume shippers who need a dedicated logistics partner with enterprise-grade reporting and account management.",
        items: [
          { title: "Dedicated account management", description: "A named account director and priority support line — your team is never on a queue." },
          { title: "Custom SLAs & volume pricing", description: "Custom service-level agreements and volume-based rates tailored to your operation." },
          { title: "Executive dashboards & reporting", description: "Live performance dashboards and audit-ready reports for every stakeholder." },
          { title: "Multi-branch coordination", description: "Coordinated delivery operations across multiple branches and locations, with consolidated reporting." },
        ],
      },
    ],
    ctaTitle: "Not sure which service fits your shipment?",
    ctaSubtitle:
      "Tell us what you're sending and where — we'll recommend the right service and give you a clear, upfront quote.",
  },
  solutions: {
    eyebrow: "Business Solutions",
    titleLead: "Delivery infrastructure for",
    titleHighlight: "growing businesses.",
    description:
      "Whether you're an online store shipping daily orders or an enterprise managing complex logistics, Twasal Awasal scales with you.",
    typicalOutcome: "Typical Outcome",
    platformSnapshot: "What you get",
    snapshotItems: [
      "Live Map & Real-Time Dispatch Board",
      "Driver Performance Scorecards",
      "Digital Proof of Delivery (POD)",
      "Automated SMS & WhatsApp Customer Alerts",
      "AI-Powered Last-Mile Route Optimisation",
    ],
    ctaTitle: "Let's build a delivery plan around your business.",
    ctaSubtitle: "Speak with our business team — we'll recommend the right plan and have you set up within days.",
    sections: [
      {
        eyebrow: "E-commerce & Retail",
        title: "E-commerce & Retail",
        description: "Daily order fulfilment with reliable COD handling, branded customer notifications, and returns management — so you can focus on growing your store.",
        bullets: [
          "Daily order pickup from your warehouse or store",
          "Reliable cash-on-delivery collection and reconciliation",
          "Branded SMS and WhatsApp updates to your customers",
          "Returns and reverse logistics managed end-to-end",
          "Direct integration with popular e-commerce platforms",
        ],
        outcome: "E-commerce clients see fewer failed deliveries and faster cash reconciliation within the first month.",
        snapshot: [
          "Daily Order Pickup",
          "COD Real-Time Reconciliation",
          "Branded Customer SMS & WhatsApp",
          "Returns & Reverse Logistics",
          "E-commerce Platform Integration",
        ],
      },
      {
        eyebrow: "SMEs & Distributors",
        title: "SMEs & Distributors",
        description: "Predictable, prepaid delivery bundles that scale as your order volume grows — with a dedicated account manager from day one.",
        bullets: [
          "Predictable prepaid monthly bundles",
          "Per-delivery rates that improve as you scale",
          "Dedicated account manager from day one",
          "Live dashboard with delivery and cost visibility",
          "Returns and exchanges fully managed",
        ],
        outcome: "Distributors consolidate multiple informal courier arrangements into one accountable, trackable partner.",
        snapshot: [
          "Prepaid Monthly Bundle",
          "Dedicated Account Manager",
          "Live Performance Dashboard",
          "Returns & Exchanges Handled",
          "Volume-Based Per-Delivery Pricing",
        ],
      },
      {
        eyebrow: "Enterprise & Corporate",
        title: "Enterprise & Corporate",
        description: "Custom SLAs, executive-level reporting, and a dedicated operations team for organisations with complex, high-volume logistics needs.",
        bullets: [
          "Custom service-level agreements tailored to your operation",
          "Executive-level reporting and quarterly business reviews",
          "Dedicated operations team and priority escalation line",
          "Multi-branch and multi-city coordination",
          "Volume-based pricing with transparent cost reporting",
        ],
        outcome: "Enterprise partners gain a single point of accountability across multi-branch and multi-city delivery operations.",
        snapshot: [
          "Custom SLAs & Volume Pricing",
          "Executive Reporting Dashboard",
          "Dedicated Operations Team",
          "Multi-Branch Coordination",
          "Priority Escalation Line",
        ],
      },
      {
        eyebrow: "Postal & Government",
        title: "Postal & Government",
        description: "Supporting national and regional postal coordination with secure handling, audit trails, and compliance-ready documentation.",
        bullets: [
          "Secure handling protocols for sensitive shipments",
          "Immutable audit trails on every shipment",
          "Compliance-ready documentation and reporting",
          "Coordinated multi-location operations",
          "Discreet, vetted driver network",
        ],
        outcome: "Public-sector partners benefit from transparent, auditable delivery records for every shipment.",
        snapshot: [
          "Secure Handling Protocols",
          "Immutable Audit Trail",
          "Compliance-Ready Reporting",
          "Multi-Location Coordination",
          "Vetted Driver Network",
        ],
      },
    ],
  },
  pricing: {
    eyebrow: "Pricing",
    titleLead: "Transparent delivery rates.",
    titleHighlight: "Built to scale.",
    description:
      "Sending one parcel or one thousand — choose the option that fits, and upgrade any time as your volume grows.",
    badges: ["Pay-as-you-go available", "Complimentary 2-week pilot for business plans", "Unused credits roll over up to 25%"],
    payAsYouGo: {
      eyebrow: "Pay As You Go",
      title: "For occasional senders.",
      description: "No account or commitment required — pay per shipment, weight-based, with full tracking and proof of delivery included.",
      footnote: "Items exceeding 20 kg or oversized shipments are quoted on request. All rates include Muscat city-wide last-mile delivery with digital proof of delivery.",
      headers: { weight: "Weight", description: "Description", market: "Market Rate", prepaid: "Twasal Awasal Rate", save: "You Save" },
      rows: [
        { weight: "Up to 3 kg", desc: "Document, small parcel", market: "OMR 2.50–3.50", prepaid: "OMR 1.80", save: "28%" },
        { weight: "3 – 5 kg", desc: "Medium parcel (shoe box)", market: "OMR 3.50–5.00", prepaid: "OMR 2.50", save: "30%" },
        { weight: "5 – 10 kg", desc: "Large parcel, appliance", market: "OMR 5.00–7.50", prepaid: "OMR 3.50", save: "30%" },
        { weight: "10 – 20 kg", desc: "Bulk / heavy item", market: "OMR 8.00–12.00", prepaid: "OMR 5.50", save: "31%" },
      ],
      ctaLabel: "Send a Parcel",
    },
    section1Eyebrow: "Business Plans",
    section1Title: "Five plans. One scalable network.",
    section1Description: "Prepaid model — your bundle is paid upfront and credits are loaded immediately. Twelve-month terms recommended for Starter and Business plans.",
    monthly: "/month",
    enrollment: "One-time enrollment",
    effective: "Effective",
    perDelivery: "per delivery",
    mostPopular: "Most Popular",
    startWith: "Start with",
    plans: [
      {
        id: "starter",
        name: "Starter",
        deliveries: "50 deliveries per month",
        tagline: "Home-based sellers, micro e-commerce businesses, and small F&B operators with a clear growth path.",
        perDelivery: "OMR 1.98",
        features: [
          "Full Muscat city and suburban zone coverage",
          "Digital dashboard — create and track orders online",
          "Shared account manager (response within 4 hours)",
          "Digital Proof of Delivery on every shipment",
          "Monthly delivery performance report",
          "Upgrade at any time — no lock-in penalties",
          "Additional deliveries: OMR 2.50 per shipment",
        ],
      },
      {
        id: "business",
        name: "Business",
        deliveries: "100 deliveries — 90 local, 10 international",
        tagline: "Growing retailers, online stores, and small importers and distributors.",
        perDelivery: "OMR 1.99",
        features: [
          "International deliveries included in your bundle",
          "Digital dashboard with live shipment tracking",
          "Shared account manager (response within 2 hours)",
          "Digital POD with automated customer notifications",
          "Freight coordination for imports and exports",
          "Monthly performance and cost breakdown report",
          "Additional deliveries: OMR 2.25 per shipment",
        ],
      },
      {
        id: "professional",
        name: "Professional",
        deliveries: "200 deliveries — 175 local, 25 international",
        tagline: "Construction firms, property developers, medical suppliers, and mid-size importers.",
        perDelivery: "OMR 1.75",
        features: [
          "Dedicated Account Manager — response within 1 hour",
          "Digital dashboard, client portal, live tracking, and reporting",
          "Weekly performance reporting (SLA, success rate, and costs)",
          "Returns and reverse logistics included, end-to-end",
          "Priority dispatch — your shipments scheduled first",
          "Additional local deliveries: OMR 1.00 per shipment",
        ],
      },
      {
        id: "enterprise",
        name: "Enterprise",
        deliveries: "350 deliveries — 300 local, 50 international",
        tagline: "Larger SMBs, construction companies, wholesale distributors, and active importers.",
        perDelivery: "OMR 1.57",
        features: [
          "Dedicated account manager with priority phone support",
          "Client dashboard with advanced analytics",
          "Weekly performance reporting and quarterly business review",
          "Warehousing coordination support included",
          "Priority dispatch — your orders first in the schedule",
          "Additional deliveries: OMR 1.75 per shipment",
        ],
      },
      {
        id: "elite",
        name: "Elite",
        deliveries: "600 deliveries — 500 local, 100 international",
        tagline: "High-volume businesses requiring a fully managed outsourced logistics operation.",
        perDelivery: "OMR 1.33",
        features: [
          "Oman-wide coverage plus all international destinations",
          "Dedicated account director and dedicated operations team",
          "Custom-branded client portal with API integration",
          "Daily reporting with real-time executive dashboard",
          "Warehousing management support (up to 200 sqm)",
          "Custom SLA agreements with a dedicated escalation line",
          "Additional deliveries: OMR 1.50 per shipment",
        ],
      },
    ],
    rateCard: {
      eyebrow: "Per-Delivery Rate Card",
      title: "Weight-based pricing — 20 to 33% below standard walk-in rates.",
      description: "Same per-delivery rates across all plans. Prepaid bundles unlock the biggest savings on every weight tier.",
      headers: { weight: "Weight", description: "Description", market: "Market Rate", prepaid: "Prepaid Bundle", save: "You Save" },
      rows: [
        { weight: "Up to 3 kg", desc: "Document, small parcel", market: "OMR 2.50–3.50", prepaid: "OMR 1.80", save: "28%" },
        { weight: "3 – 5 kg", desc: "Medium parcel (shoe box)", market: "OMR 3.50–5.00", prepaid: "OMR 2.50", save: "30%" },
        { weight: "5 – 10 kg", desc: "Large parcel, appliance", market: "OMR 5.00–7.50", prepaid: "OMR 3.50", save: "30%" },
        { weight: "10 – 20 kg", desc: "Bulk / heavy item", market: "OMR 8.00–12.00", prepaid: "OMR 5.50", save: "31%" },
      ],
      footer: "Items exceeding 20 kg or oversized shipments are quoted on request. All rates include Muscat city-wide last-mile delivery with digital Proof of Delivery.",
    },
    billing: {
      prepaidEyebrow: "Prepaid Account",
      prepaidTitle: "Available on all plans from day one",
      prepaidItems: [
        "Pay your monthly bundle upfront — credits loaded immediately",
        "Use deliveries at any point during the calendar month",
        "Top up at your bundle rate if you require additional deliveries",
        "Unused credits roll over for up to 30 days (maximum 25% of bundle value)",
        "Full dashboard access: upload shipments, track deliveries, and monitor spend in real time",
      ],
      postpaidEyebrow: "Postpaid Account",
      postpaidTitle: "Available to high-volume clients by application",
      postpaidItems: [
        "Minimum 200 deliveries per month required to qualify",
        "Biweekly billing cycle — invoices issued every 14 days",
        "Itemised invoice per delivery, broken down by weight tier and destination",
        "Same bundle rates as prepaid — no postpaid surcharge applied",
        "Credit approval requires a signed agreement and a 2-week prepaid trial",
      ],
    },
    ctaTitle: "Begin your complimentary two-week pilot",
    ctaSubtitle: "No commitment. No risk. Full access to the Twasal Awasal platform — dashboard, live tracking, digital Proof of Delivery, and reporting — included from day one.",
  },
  about: {
    eyebrow: "About Twasal Awasal",
    titleLead: "About",
    titleHighlight: "Twasal Awasal.",
    description: "Twasal Awasal is a licensed Omani delivery and postal service provider, built on a foundation of AI-driven routing, real-time tracking, and digital proof of delivery. We move parcels, cargo, and freight across Muscat, the GCC, and beyond — for individuals sending a single parcel and businesses shipping at scale.",
    tagline: "Oman's AI-powered delivery network.",
    missionTitle: "Our Mission",
    missionBody: "To give every individual and business in Oman a delivery experience that is fast, transparent, and dependable — powered by technology, delivered by people who know the region.",
    visionTitle: "Our Vision",
    visionBody: "A Sultanate where every parcel — local, regional, or international — is tracked, verified, and delivered with complete confidence, and where Twasal Awasal is the name Omanis and GCC businesses trust first.",
    valuesEyebrow: "What We Stand For",
    valuesTitle: "Three principles we will not compromise on.",
    valuesDescription: "They show up in every shipment we plan, every route we run, and every customer we serve.",
    values: [
      { title: "Reliability First", description: "We measure ourselves on on-time delivery and customer satisfaction — not on the number of services we list." },
      { title: "Technology With Purpose", description: "AI and automation exist to make every delivery faster and more transparent — not as a feature for its own sake." },
      { title: "Built for Oman, Ready for the Region", description: "Licensed and compliant at home, with the network and technology to grow across the GCC." },
    ],
    journeyEyebrow: "Our Journey",
    journeyTitle: "Built with purpose. Growing with the region.",
    journeyDescription: "",
    milestones: [
      { year: "2023", title: "Founded in Muscat", text: "Twasal Awasal established to bring modern, technology-driven delivery to Omani individuals and businesses." },
      { year: "2024", title: "Digital Platform Live", text: "Live GPS tracking, digital proof of delivery, and online booking launched for customers across Muscat." },
      { year: "2025", title: "Regional Expansion", text: "Cross-border cargo and freight services launched across the GCC, alongside first enterprise contracts." },
      { year: "2026", title: "AI Routing Launched", text: "AI-driven route planning and ETA prediction rolled out network-wide, cutting delivery times across all service lines." },
    ],
    teamEyebrow: "The Network",
    teamTitleLead: "Licensed in Oman.",
    teamTitleHighlight: "Ready for the region.",
    teamBody1: "Twasal Awasal is a fully licensed delivery and postal service provider headquartered in Muscat, with cross-border reach across the GCC.",
    teamBody2: "Every route is AI-optimised. Every delivery is tracked. Every shipment is compliance-ready.",
    teamCards: [
      { stat: "Muscat", sub: "Headquarters" },
      { stat: "GCC", sub: "Regional Reach" },
      { stat: "AI-Powered", sub: "Every Route" },
      { stat: "Licensed", sub: "Fully Compliant" },
    ],
    ctaTitle: "Experience the smarter way to ship.",
    ctaSubtitle: "Whether you're sending a single parcel or managing enterprise logistics, Twasal Awasal is ready.",
  },
  partner: {
    eyebrow: "Contact",
    titleLead: "Let's get your",
    titleHighlight: "delivery sorted.",
    titleTrail: "",
    description: "Whether you're sending a personal parcel or setting up a business account, our team is ready to help.",
    contactEyebrow: "Talk to our team",
    contactTitle: "We're here to help.",
    contactBody: "Send us a quick message — our team will reply within one business day. For urgent shipments, please use the tracking page or write directly to hello@twasalawasal.com.",
    headquarters: "Headquarters",
    hqLocation: "Muscat, Sultanate of Oman",
    partnershipsLabel: "General enquiries",
    businessLabel: "Business enquiries",
    pilotEyebrow: "Need to track a shipment?",
    pilotBody: "Already have a tracking number? Skip the form — head straight to the track shipment page for live, real-time status.",
    form: {
      name: "Full name",
      namePh: "Your name",
      email: "Email",
      emailPh: "you@example.com",
      company: "Company (optional)",
      companyPh: "Your company",
      phone: "Phone (optional)",
      phonePh: "+968 …",
      plan: "Enquiry type",
      planPh: "Select enquiry type",
      volume: "Monthly delivery volume (business only)",
      volumePh: "Select volume",
      message: "Tell us about your enquiry",
      messagePh: "What you're sending, where, and any other details...",
      consent: "By submitting this form, you consent to being contacted by Twasal Awasal in connection with your enquiry.",
    },
    planOptions: [
      "Send a parcel",
      "Business account",
      "Track an existing shipment",
      "Other enquiry",
    ],
    volumeOptions: [
      "Fewer than 100 deliveries per month",
      "100–500 deliveries per month",
      "500–2,000 deliveries per month",
      "More than 2,000 deliveries per month",
    ],
    success: {
      title: "Enquiry received.",
      body: "Thank you for reaching out. Our team will be in touch within one business day.",
    },
    errorFallback: "Something went wrong. Please try again, or contact us directly at hello@twasalawasal.com.",
  },
  track: {
    eyebrow: "Track Shipment",
    title: "Track Your Shipment",
    subtitle: "Enter your tracking number below for live, real-time status — from pickup to delivery.",
    placeholder: "Enter tracking number",
    button: "Track Now",
    statusSteps: [
      "Order Placed",
      "Picked Up",
      "In Transit",
      "Out for Delivery",
      "Delivered",
    ],
    supportTitle: "Can't find your shipment?",
    supportBody: "Double-check your tracking number, or contact our support team — we're happy to help.",
    supportCta: "Contact Support",
    notFoundMessage: "We couldn't find a shipment matching that tracking number. Please check the number and try again, or contact support for help.",
    yourShipment: "Your shipment",
    estimatedDelivery: "Estimated delivery",
    lastUpdate: "Last update",
    fromTo: "Route",
  },
  login: {
    eyebrow: "Dashboard Login",
    title: "Dashboard launching soon.",
    body: "The Twasal Awasal customer portal is rolling out in waves. Already onboarded? Your account manager will share direct access credentials shortly.",
    note: "Next access wave: rolling — by activation date",
  },
  footer: {
    tagline: "Oman's AI-powered delivery network — moving every parcel, cargo, and shipment, smarter.",
    platform: "Quick Links",
    company: "Company",
    contact: "Contact",
    links: {
      features: "Services",
      solutions: "Business Solutions",
      pricing: "Pricing",
      track: "Track Shipment",
      login: "Login",
      about: "About Us",
      partner: "Contact",
      careers: "Careers",
    },
    location: "Muscat, Sultanate of Oman",
    copyright: "© {year} Twasal Awasal. All rights reserved. Muscat, Sultanate of Oman.",
    motto: "One platform. Every delivery. Total visibility.",
    socialLabel: "Follow us",
  },
};


const ar = {
  meta: {
    languageName: "Arabic",
    arName: "العربية",
  },
  nav: {
    home: "الرئيسية",
    features: "الخدمات",
    services: "الخدمات",
    solutions: "الحلول للأعمال",
    track: "تتبع الشحنة",
    pricing: "الأسعار",
    about: "عن الشركة",
    partner: "تواصل معنا",
    contact: "تواصل معنا",
    login: "الدخول إلى لوحة التحكم",
  },
  common: {
    trackShipment: "تتبع شحنتك",
    getBusinessQuote: "احصل على عرض للأعمال",
    sendParcel: "أرسل طردًا",
    partnerWithUs: "تواصل معنا",
    seeHowItWorks: "اكتشف كيف يعمل",
    viewPricing: "عرض الأسعار",
    learnMore: "اعرف المزيد",
    sendEnquiry: "إرسال الاستفسار",
    sending: "جارٍ الإرسال...",
    sendAnother: "إرسال استفسار جديد",
    backHome: "العودة إلى الرئيسية",
    becomePartner: "ابدأ الآن",
    viewAllFeatures: "عرض جميع الخدمات",
    startConversation: "ابدأ المحادثة",
    startPilot: "ابدأ الفترة التجريبية",
    poweredByDelfi: "Twasal Awasal",
  },
  hero: {
    eyebrow: "محلي · شحن · دولي · مدعوم بالذكاء الاصطناعي",
    headline1: "كل توصيل،",
    headline2: "",
    highlight: "أذكى.",
    subheadline:
      "Twasal Awasal هي شبكة عُمان للتوصيل والبريد المعتمدة على التكنولوجيا — تنقل الطرود والبضائع والشحن عبر مسقط ودول الخليج وما بعدها. كل شحنة تُخطَّط وتُتتبع وتُسلَّم باستخدام تقنيات الذكاء الاصطناعي من البداية إلى النهاية.",
    ctaPrimary: "تتبع شحنتك",
    ctaSecondary: "احصل على عرض للأعمال",
    trust: "توصيل الميل الأخير · شحن ومناولة · شحن دولي · توصيل التجارة الإلكترونية · لوجستيات المؤسسات",
    serviceModes: ["الميل الأخير المحلي", "الشحن والبضائع", "الشحن الدولي"],
    sceneLabel: "شبكة مباشرة · مسقط ← العالم",
    activeBadge: "مسارات ذكية",
    zoneBadge: "متعدد الوسائط",
    notifTitle: "نشاط الشبكة المباشر",
    notifJustNow: "الآن",
    notifEtaUpdates: "تحديث الشبكة كل ثانية",
    notifications: [
      { title: "تم تأكيد الاستلام — مطرح، مسقط", meta: "تم تعيين السائق · في الطريق · الوصول خلال ٦ دقائق" },
      { title: "بضائع في الطريق — ميناء صحار ← دبي", meta: "الشاحنة مغلقة · انطلقت الساعة ٠٨:٤٢ بتوقيت الخليج" },
      { title: "شحنة دولية في الطريق — مسقط ← مطار هيثرو", meta: "على متن الطائرة · مدة الرحلة ٧ س ٢٠ د · في الموعد" },
      { title: "تم التسليم — إثبات التسليم مسجل", meta: "الصورة والتوقيع محفوظان · ١٤:١٧ بتوقيت الخليج" },
    ],
    trackWidget: {
      title: "أين طردك؟",
      sub: "أدخل رقم التتبع لمعرفة حالته اللحظية.",
      placeholder: "أدخل رقم التتبع (مثال: TWA123456789OM)",
      button: "تتبع الآن",
    },
    stats: [
      "تغطية جميع أحياء مسقط",
      "امتداد عبر دول الخليج",
      "مسارات محسّنة بالذكاء الاصطناعي لكل شحنة",
      "إثبات تسليم رقمي لكل شحنة",
    ],
    forYou: {
      heading: "هل ترسل طردًا؟",
      body: "احجز الاستلام في دقائق، تتبعه مباشرة، واحصل على إثبات لحظة تسليمه — سواء داخل مسقط أو عبر الحدود.",
      cta: "أرسل طردًا",
    },
    forBusiness: {
      heading: "تشحن بكميات كبيرة؟",
      body: "من طلبات التجارة الإلكترونية اليومية إلى عقود الشحن للمؤسسات، توفر لك خططنا الخاصة بالأعمال دعمًا مخصصًا وتقارير وأسعارًا تنمو مع أعمالك.",
      cta: "احصل على عرض للأعمال",
    },
    phone: {
      greeting: "مرحبًا، أحمد",
      prompt: "ماذا ترسل اليوم؟",
      actions: [
        { title: "توصيل فوري", sub: "استلام وتسليم فوري" },
        { title: "نهاية اليوم", sub: "التسليم قبل المساء" },
        { title: "توصيل بشاحنة", sub: "للأغراض الكبيرة والثقيلة" },
        { title: "جدولة", sub: "اختر التاريخ والوقت" },
      ],
      recentOrders: "الطلبات الأخيرة",
      viewAll: "عرض الكل",
      orders: [
        { from: "سوق مطرح", to: "شاطئ القرم", amount: "٦٫٤٠٩", status: "مؤكد" },
        { from: "جامع السلطان قابوس", to: "سوق مطرح", amount: "٨٫٣٦٦", status: "مؤكد" },
        { from: "شاطئ القرم", to: "روي للأعمال", amount: "٥٫١١٦", status: "مؤكد" },
      ],
      tabs: ["الرئيسية", "طلباتي", "الدعم", "الملف الشخصي"],
      location: "مسقط، عُمان",
    },
  },
  pillars: {
    eyebrow: "لماذا Twasal Awasal",
    title: "مبنية على التكنولوجيا. مقدَّمة بأيدي بشرية.",
    description:
      "نجمع بين شبكة توصيل عُمانية مرخّصة وتقنيات مدعومة بالذكاء الاصطناعي — بحيث تسلك كل شحنة أقصر مسار، وتظل مرئية في كل خطوة، وتصل بأيدٍ آمنة.",
    items: [
      { title: "توجيه ذكي بالذكاء الاصطناعي", description: "يحسب نظامنا باستمرار أسرع وأكثر المسارات كفاءة لكل سائق — مع مراعاة حركة المرور المباشرة، ونوافذ التسليم، وحمولة المركبة." },
      { title: "تتبع لحظي", description: "كل شحنة مرئية على خريطة مباشرة من الاستلام حتى التسليم. تعلم دائمًا أين طردك بالضبط." },
      { title: "إثبات تسليم رقمي", description: "صورة وتوقيع وطابع زمني في كل عملية تسليم — محفوظة بأمان ومتاحة لحظة الحاجة إليها." },
    ],
  },
  homeFeatures: {
    eyebrow: "لك ولأعمالك",
    title: "شبكة واحدة. تناسب كل مرسل.",
    description: "من الأفراد الذين يرسلون طردًا واحدًا إلى الأعمال التي تشحن بكميات كبيرة — Twasal Awasal مصممة لك.",
    items: [
      { title: "هل ترسل طردًا؟", description: "احجز الاستلام في دقائق، تتبعه مباشرة، واحصل على إثبات التسليم لحظة الاستلام — سواء داخل مسقط أو عبر الحدود." },
      { title: "تشحن بكميات كبيرة؟", description: "من طلبات التجارة الإلكترونية اليومية إلى عقود الشحن للمؤسسات، توفر لك خططنا دعمًا مخصصًا وتقارير وأسعارًا تنمو مع أعمالك." },
      { title: "تتبع لحظي", description: "دقة GPS لحظية لكل شحنة نشطة. أنت وعملاؤك تعرفون أين الطرد بالضبط — دون الحاجة إلى مكالمة هاتفية." },
      { title: "تحسين المسارات بالذكاء الاصطناعي", description: "مسارات أذكى. عدد أقل من محاولات التسليم الفاشلة. يتعلم محرك التوجيه لدينا من حركة المرور ونوافذ التسليم وسجل السائقين." },
      { title: "إثبات تسليم رقمي", description: "صورة وتوقيع وطابع زمني عند الباب — محفوظة بأمان وقابلة للتصدير بنقرة واحدة." },
      { title: "إشعارات العملاء", description: "رسائل SMS وواتساب تلقائية عند كل مرحلة — مخصصة لعلامتك، وفورية، ودون أي جهد من فريقك." },
    ],
  },
  homeQuote: {
    eyebrow: "شهادة ثقة",
    quote1: "توفر Twasal Awasal لعملائنا التتبع المباشر والموثوقية التي يتوقعونها — وتمنحنا",
    quote2: "الأساس التشغيلي للاستمرار في النمو",
    quote3: " في جميع أنحاء عُمان.",
    attribution: "— أحد عملاء Twasal Awasal من قطاع الأعمال",
  },
  ctaBanner: {
    eyebrow: "جاهز للإرسال",
    title: "جاهز للشحن بذكاء؟",
    subtitle: "طرد واحد أو ألف طرد، Twasal Awasal توصلها — متتبعة وموثقة وفي وقتها.",
    primary: "تتبع شحنتك",
    secondary: "احصل على عرض للأعمال",
    bulletsHeading: "Twasal Awasal مناسبة لك إذا:",
    bullets: [
      "تحتاج إلى إرسال طرد واحد عبر مسقط — في نفس اليوم أو اليوم التالي",
      "تدير متجرًا إلكترونيًا وتحتاج إلى توصيل موثوق وإدارة الدفع عند الاستلام وتتبع مباشر",
      "تشحن ٥٠ إلى ٦٠٠+ طردًا شهريًا وتحتاج إلى أسعار أفضل ودعم حساب مخصص",
      "أنت جهة حكومية أو شريك مؤسسي وتحتاج إلى تقارير قابلة للتدقيق لكل شحنة",
    ],
  },
  appDownload: {
    eyebrow: "تطبيق الجوال",
    headlineLead: "شحناتك.",
    headlineHighlight: "في جيبك.",
    body:
      "يضع تطبيق Twasal Awasal كل شحنة في يدك. احجز استلامًا خلال ثوانٍ. تتبع مباشرة. اطلع على إثبات التسليم لحظة وصوله. عربي وإنجليزي. iPhone وAndroid.",
    appleSubtitle: "حمّل من",
    appleTitle: "App Store",
    googleSubtitle: "احصل عليه من",
    googleTitle: "Google Play",
    appTagline: "كل طرد · كل مسافة",
    bullets: [
      "الحجز — لمسة واحدة للحجز. في نفس اليوم أو مجدول خلال أقل من ٣٠ ثانية.",
      "التتبع المباشر — تتبع GPS لحظي. اعرف قبل أن يسألك عميلك.",
      "إثبات التسليم — إثبات تسليم رقمي عند الباب. لا ورق ولا نزاعات.",
      "الإشعارات — تنبيهات آلية. SMS وواتساب، مخصصة لعلامتك، فورية.",
    ],
  },
  features: {
    eyebrow: "الخدمات",
    titleLead: "كل أنواع التوصيل.",
    titleHighlight: "شبكة موثوقة واحدة.",
    description:
      "من طرد واحد عبر مسقط إلى شحن يعبر الحدود، Twasal Awasal تنقلها كلها — مع تخطيط ذكي مدعوم بالذكاء الاصطناعي خلف كل شحنة.",
    groups: [
      {
        title: "الميل الأخير والتوصيل المحلي",
        eyebrow: "محلي",
        description: "توصيل في نفس اليوم واليوم التالي عبر مسقط ومدن عُمان الرئيسية. مصمم للطرود اليومية والوثائق والتوصيلات ذات الأولوية الزمنية.",
        items: [
          { title: "توصيل في نفس اليوم داخل مسقط", description: "احجز قبل الساعة ١٢:٠٠ ظهرًا للتسليم في نفس اليوم في أي مكان بمحافظة مسقط." },
          { title: "توصيل في اليوم التالي عبر عُمان", description: "تغطية موثوقة في اليوم التالي إلى صحار ونزوى وصلالة وجميع مدن عُمان الكبرى." },
          { title: "تتبع GPS مباشر لكل رحلة", description: "تحديثات GPS لحظية لكل شحنة نشطة، متاحة للمرسل والمستلم." },
          { title: "إثبات تسليم رقمي", description: "صورة وتوقيع وطابع زمني عند الباب — قابلة للتصدير بنقرة واحدة." },
        ],
      },
      {
        title: "الشحن والبضائع",
        eyebrow: "شحن",
        description: "شحنات أثقل وبضائع بالجملة تنتقل عبر عُمان ودول الخليج — من منصة واحدة إلى شاحنة كاملة.",
        items: [
          { title: "شحن محلي وعبر الحدود الخليجية", description: "من منصة واحدة إلى شاحنة كاملة، تنتقل عبر عُمان وممرات الخليج." },
          { title: "خيارات مركبات مرنة", description: "مركبات بالحجم المناسب للحمولات الكبيرة والحجوم الاستثنائية والشحنات الخاصة." },
          { title: "تحديثات لحظية وتوقيت الوصول", description: "تحديثات مستمرة للحالة وأوقات وصول دقيقة من الاستلام حتى التسليم." },
          { title: "تنسيق مخصص", description: "منسق مخصص لكل شحنة عاجلة أو عالية القيمة." },
        ],
      },
      {
        title: "الشحن الدولي",
        eyebrow: "دولي",
        description: "أرسل الوثائق والطرود والبضائع إلى وجهات رئيسية حول العالم مع خدمة تخليص جمركي شاملة.",
        items: [
          { title: "توصيل الطرود والوثائق الدولية", description: "شحن موثوق إلى وجهات رئيسية في أوروبا وآسيا والأمريكتين وأفريقيا." },
          { title: "التخليص الجمركي والوثائق", description: "يتولى فريقنا كافة الأوراق الجمركية — أنت تشحن، ونحن نُخلّص." },
          { title: "تتبع شامل", description: "رقم تتبع واحد من عُمان إلى الوجهة — رؤية كاملة في كل مرحلة." },
          { title: "أسعار دولية تنافسية", description: "أسعار مفاوض عليها بالحجم على جميع الممرات الدولية الرئيسية." },
        ],
      },
      {
        title: "التجارة الإلكترونية والدفع عند الاستلام",
        eyebrow: "تجارة إلكترونية",
        description: "مصممة للبائعين عبر الإنترنت الذين يحتاجون إلى تنفيذ موثوق وإدارة شفافة للدفع عند الاستلام — دون إدارة أسطول توصيل بأنفسهم.",
        items: [
          { title: "دفع عند الاستلام مع تسوية فورية", description: "تحصيل ومتابعة وتسوية — مع سجل تدقيق كامل لكل معاملة نقدية." },
          { title: "تحديثات SMS وواتساب تلقائية", description: "إشعارات مخصصة لعلامتك عند كل مرحلة — دون أي جهد من فريقك." },
          { title: "الإرجاعات واللوجستيات العكسية", description: "إدارة كاملة للإرجاعات، شاملة الاستلام والفحص وتنسيق الاسترداد." },
          { title: "تكامل مع منصات التجارة الإلكترونية", description: "تكامل مباشر مع منصات التجارة الإلكترونية الشائعة — الطلبات تنتقل مباشرة إلى الإرسال." },
        ],
      },
      {
        title: "حلول المؤسسات والشبكات البريدية",
        eyebrow: "مؤسسات",
        description: "للشركات والجهات الحكومية والشحنة عالية الحجم الذين يحتاجون شريكًا لوجستيًا مخصصًا مع تقارير وإدارة حسابات بمستوى المؤسسات.",
        items: [
          { title: "إدارة حساب مخصصة", description: "مدير حساب مخصص وخط دعم ذو أولوية — فريقك ليس في قائمة انتظار أبدًا." },
          { title: "اتفاقيات SLA مخصصة وأسعار الحجم", description: "اتفاقيات مستوى خدمة مصممة وأسعار قائمة على الحجم تناسب عملياتك." },
          { title: "لوحات تنفيذية وتقارير", description: "لوحات أداء مباشرة وتقارير جاهزة للتدقيق لكل صانع قرار." },
          { title: "تنسيق متعدد الفروع", description: "عمليات توصيل منسقة عبر فروع ومواقع متعددة، مع تقارير موحدة." },
        ],
      },
    ],
    ctaTitle: "لست متأكدًا أي خدمة تناسب شحنتك؟",
    ctaSubtitle:
      "أخبرنا ماذا ترسل وإلى أين — سنوصي بالخدمة المناسبة ونقدم لك عرض سعر واضحًا مقدمًا.",
  },
  solutions: {
    eyebrow: "الحلول للأعمال",
    titleLead: "بنية توصيل",
    titleHighlight: "للأعمال النامية.",
    description:
      "سواء كنت متجرًا إلكترونيًا يشحن طلبات يومية أو مؤسسة تدير لوجستيات معقدة، تنمو Twasal Awasal معك.",
    typicalOutcome: "النتيجة المتوقعة",
    platformSnapshot: "ما ستحصل عليه",
    snapshotItems: [
      "خريطة مباشرة ولوحة إرسال لحظية",
      "بطاقات أداء السائقين",
      "إثبات تسليم رقمي",
      "تنبيهات SMS وواتساب تلقائية للعملاء",
      "تحسين مسار الميل الأخير بالذكاء الاصطناعي",
    ],
    ctaTitle: "لنبنِ خطة توصيل تناسب أعمالك.",
    ctaSubtitle: "تحدث مع فريقنا للأعمال — سنوصي بالخطة المناسبة ونجهزك خلال أيام.",
    sections: [
      {
        eyebrow: "التجارة الإلكترونية والتجزئة",
        title: "التجارة الإلكترونية والتجزئة",
        description: "تنفيذ الطلبات اليومية مع إدارة موثوقة للدفع عند الاستلام، وإشعارات عملاء مخصصة لعلامتك، وإدارة الإرجاعات — لتتمكن من التركيز على نمو متجرك.",
        bullets: [
          "استلام يومي للطلبات من مستودعك أو متجرك",
          "تحصيل وتسوية موثوقة للدفع عند الاستلام",
          "تحديثات SMS وواتساب لعملائك مخصصة لعلامتك",
          "إدارة كاملة للإرجاعات واللوجستيات العكسية",
          "تكامل مباشر مع منصات التجارة الإلكترونية الشائعة",
        ],
        outcome: "يشهد عملاء التجارة الإلكترونية انخفاضًا في محاولات التسليم الفاشلة وتسوية أسرع للمبالغ النقدية في الشهر الأول.",
        snapshot: [
          "استلام يومي للطلبات",
          "تسوية لحظية للدفع عند الاستلام",
          "SMS وواتساب مخصص للعملاء",
          "إدارة الإرجاعات واللوجستيات العكسية",
          "تكامل مع منصات التجارة الإلكترونية",
        ],
      },
      {
        eyebrow: "الشركات الصغيرة والموزعون",
        title: "الشركات الصغيرة والموزعون",
        description: "حزم توصيل شهرية مدفوعة مسبقًا يمكن التنبؤ بها تنمو مع حجم طلباتك — مع مدير حساب مخصص منذ اليوم الأول.",
        bullets: [
          "حزم شهرية مدفوعة مسبقًا يمكن التنبؤ بها",
          "أسعار لكل عملية توصيل تتحسن مع نموّك",
          "مدير حساب مخصص منذ اليوم الأول",
          "لوحة تحكم مباشرة لرؤية التوصيلات والتكاليف",
          "إدارة كاملة للإرجاعات والاستبدال",
        ],
        outcome: "يوحّد الموزعون ترتيبات الشحن غير الرسمية المتعددة في شريك واحد قابل للمساءلة والتتبع.",
        snapshot: [
          "حزمة شهرية مدفوعة مسبقًا",
          "مدير حساب مخصص",
          "لوحة أداء مباشرة",
          "إدارة الإرجاعات والاستبدال",
          "أسعار قائمة على حجم التوصيل",
        ],
      },
      {
        eyebrow: "المؤسسات والشركات",
        title: "المؤسسات والشركات",
        description: "اتفاقيات SLA مخصصة، وتقارير على مستوى تنفيذي، وفريق عمليات مخصص للمؤسسات ذات الاحتياجات اللوجستية المعقدة والعالية الحجم.",
        bullets: [
          "اتفاقيات مستوى خدمة مصممة وفق عملياتك",
          "تقارير على مستوى تنفيذي ومراجعات ربع سنوية",
          "فريق عمليات مخصص وخط تصعيد ذو أولوية",
          "تنسيق متعدد الفروع والمدن",
          "أسعار قائمة على الحجم مع تقارير تكلفة شفافة",
        ],
        outcome: "يحصل الشركاء المؤسسيون على نقطة مساءلة واحدة عبر عمليات التوصيل متعددة الفروع والمدن.",
        snapshot: [
          "اتفاقيات SLA مخصصة وأسعار الحجم",
          "لوحة تقارير تنفيذية",
          "فريق عمليات مخصص",
          "تنسيق متعدد الفروع",
          "خط تصعيد ذو أولوية",
        ],
      },
      {
        eyebrow: "البريد والقطاع الحكومي",
        title: "البريد والقطاع الحكومي",
        description: "دعم تنسيق البريد الوطني والإقليمي بمناولة آمنة وسجلات تدقيق ووثائق جاهزة للامتثال.",
        bullets: [
          "بروتوكولات مناولة آمنة للشحنات الحساسة",
          "سجلات تدقيق ثابتة لكل شحنة",
          "وثائق وتقارير جاهزة للامتثال",
          "عمليات منسقة متعددة المواقع",
          "شبكة سائقين موثوقة ومعتمدة",
        ],
        outcome: "يستفيد الشركاء في القطاع العام من سجلات توصيل شفافة وقابلة للتدقيق لكل شحنة.",
        snapshot: [
          "بروتوكولات مناولة آمنة",
          "سجل تدقيق ثابت",
          "تقارير جاهزة للامتثال",
          "تنسيق متعدد المواقع",
          "شبكة سائقين موثوقة",
        ],
      },
    ],
  },
  pricing: {
    eyebrow: "الأسعار",
    titleLead: "أسعار توصيل شفافة.",
    titleHighlight: "قابلة للتوسع.",
    description:
      "طرد واحد أو ألف طرد — اختر الخيار الذي يناسبك، وقم بالترقية في أي وقت مع نمو حجم شحناتك.",
    badges: ["الدفع عند الطلب متاح", "تجربة مجانية لأسبوعين لخطط الأعمال", "الأرصدة غير المستخدمة تُرحَّل حتى ٢٥٪"],
    payAsYouGo: {
      eyebrow: "الدفع عند الطلب",
      title: "للمرسلين العرضيين.",
      description: "لا حاجة لحساب أو التزام — ادفع لكل شحنة حسب الوزن، مع التتبع الكامل وإثبات التسليم.",
      footnote: "الشحنات التي تتجاوز ٢٠ كجم أو ذات الأحجام الاستثنائية تُسعَّر عند الطلب. تشمل جميع الأسعار توصيل الميل الأخير في مسقط مع إثبات التسليم الرقمي.",
      headers: { weight: "الوزن", description: "الوصف", market: "سعر السوق", prepaid: "سعر Twasal Awasal", save: "توفيرك" },
      rows: [
        { weight: "حتى ٣ كجم", desc: "وثيقة، طرد صغير", market: "٢٫٥٠ – ٣٫٥٠ ر.ع.", prepaid: "١٫٨٠ ر.ع.", save: "٢٨٪" },
        { weight: "٣ – ٥ كجم", desc: "طرد متوسط (علبة أحذية)", market: "٣٫٥٠ – ٥٫٠٠ ر.ع.", prepaid: "٢٫٥٠ ر.ع.", save: "٣٠٪" },
        { weight: "٥ – ١٠ كجم", desc: "طرد كبير، جهاز", market: "٥٫٠٠ – ٧٫٥٠ ر.ع.", prepaid: "٣٫٥٠ ر.ع.", save: "٣٠٪" },
        { weight: "١٠ – ٢٠ كجم", desc: "شحنة ثقيلة / حجم كبير", market: "٨٫٠٠ – ١٢٫٠٠ ر.ع.", prepaid: "٥٫٥٠ ر.ع.", save: "٣١٪" },
      ],
      ctaLabel: "أرسل طردًا",
    },
    section1Eyebrow: "خطط الأعمال",
    section1Title: "خمس خطط. شبكة واحدة قابلة للتوسع.",
    section1Description: "نموذج مدفوع مسبقًا — تُدفع الحزمة مقدمًا وتُحمَّل الأرصدة فورًا. تُوصى بشروط اثني عشر شهرًا لخطط Starter وBusiness.",
    plans: [
      {
        id: "starter",
        name: "Starter — البداية",
        deliveries: "٥٠ توصيلة شهريًا",
        tagline: "البائعون من المنزل، والتجارة الإلكترونية الصغيرة، والمطاعم الصغيرة ذات مسار نمو واضح.",
        perDelivery: "١٫٩٨ ر.ع.",
        features: [
          "تغطية كاملة لمدينة مسقط ومناطقها الضاحية",
          "لوحة رقمية — إنشاء وتتبع الطلبات عبر الإنترنت",
          "مدير حساب مشترك (رد خلال ٤ ساعات)",
          "إثبات تسليم رقمي لكل شحنة",
          "تقرير أداء شهري",
          "الترقية في أي وقت — دون رسوم إلغاء",
          "توصيلات إضافية: ٢٫٥٠ ر.ع. للشحنة",
        ],
      },
      {
        id: "business",
        name: "Business — الأعمال",
        deliveries: "١٠٠ توصيلة — ٩٠ محلية، ١٠ دولية",
        tagline: "تجار التجزئة الناميون، والمتاجر الإلكترونية، وصغار المستوردين والموزعين.",
        perDelivery: "١٫٩٩ ر.ع.",
        features: [
          "توصيلات دولية مضمّنة في الحزمة",
          "لوحة رقمية مع تتبع مباشر للشحنات",
          "مدير حساب مشترك (رد خلال ساعتين)",
          "إثبات تسليم رقمي مع إشعارات تلقائية للعملاء",
          "تنسيق شحن للواردات والصادرات",
          "تقرير شهري للأداء وتفصيل التكاليف",
          "توصيلات إضافية: ٢٫٢٥ ر.ع. للشحنة",
        ],
      },
      {
        id: "professional",
        name: "Professional — المحترف",
        deliveries: "٢٠٠ توصيلة — ١٧٥ محلية، ٢٥ دولية",
        tagline: "شركات المقاولات، والمطورون العقاريون، وموردو المستلزمات الطبية، والمستوردون متوسطو الحجم.",
        perDelivery: "١٫٧٥ ر.ع.",
        features: [
          "مدير حساب مخصص — رد خلال ساعة واحدة",
          "لوحة رقمية وبوابة عملاء وتتبع وتقارير مباشرة",
          "تقرير أداء أسبوعي (SLA ومعدل النجاح والتكاليف)",
          "إدارة كاملة للإرجاعات واللوجستيات العكسية",
          "إرسال ذو أولوية — شحناتك مجدولة أولًا",
          "توصيلات محلية إضافية: ١٫٠٠ ر.ع. للشحنة",
        ],
      },
      {
        id: "enterprise",
        name: "Enterprise — المؤسسات",
        deliveries: "٣٥٠ توصيلة — ٣٠٠ محلية، ٥٠ دولية",
        tagline: "الشركات المتوسطة والكبرى، والمقاولات، وتجار الجملة، والمستوردون النشطون.",
        perDelivery: "١٫٥٧ ر.ع.",
        features: [
          "مدير حساب مخصص مع دعم هاتفي ذي أولوية",
          "لوحة عملاء مع تحليلات متقدمة",
          "تقارير أداء أسبوعية ومراجعة أعمال ربع سنوية",
          "دعم تنسيق التخزين مضمّن",
          "إرسال ذو أولوية — طلباتك أولًا في الجدول",
          "توصيلات إضافية: ١٫٧٥ ر.ع. للشحنة",
        ],
      },
      {
        id: "elite",
        name: "Elite — النخبة",
        deliveries: "٦٠٠ توصيلة — ٥٠٠ محلية، ١٠٠ دولية",
        tagline: "الشركات ذات الحجم العالي التي تحتاج إلى عملية لوجستية خارجية مُدارة بالكامل.",
        perDelivery: "١٫٣٣ ر.ع.",
        features: [
          "تغطية على مستوى عُمان وجميع الوجهات الدولية",
          "مدير حساب رفيع وفريق عمليات مخصص",
          "بوابة عملاء بعلامتك مع تكامل API",
          "تقارير يومية مع لوحة تنفيذية مباشرة",
          "دعم إدارة التخزين (حتى ٢٠٠ م²)",
          "اتفاقيات SLA مخصصة مع خط تصعيد مخصص",
          "توصيلات إضافية: ١٫٥٠ ر.ع. للشحنة",
        ],
      },
    ],
    rateCard: {
      eyebrow: "جدول أسعار التوصيل",
      title: "أسعار حسب الوزن — أقل من ٢٠ إلى ٣٣٪ من أسعار السوق العادية.",
      description: "نفس الأسعار لكل توصيلة في جميع الخطط. تفتح الحزم المدفوعة مسبقًا أكبر توفيرات في كل فئة وزن.",
      headers: { weight: "الوزن", description: "الوصف", market: "سعر السوق", prepaid: "الحزمة المدفوعة", save: "توفيرك" },
      rows: [
        { weight: "حتى ٣ كجم", desc: "وثيقة، طرد صغير", market: "٢٫٥٠ – ٣٫٥٠ ر.ع.", prepaid: "١٫٨٠ ر.ع.", save: "٢٨٪" },
        { weight: "٣ – ٥ كجم", desc: "طرد متوسط (علبة أحذية)", market: "٣٫٥٠ – ٥٫٠٠ ر.ع.", prepaid: "٢٫٥٠ ر.ع.", save: "٣٠٪" },
        { weight: "٥ – ١٠ كجم", desc: "طرد كبير، جهاز", market: "٥٫٠٠ – ٧٫٥٠ ر.ع.", prepaid: "٣٫٥٠ ر.ع.", save: "٣٠٪" },
        { weight: "١٠ – ٢٠ كجم", desc: "شحنة ثقيلة / حجم كبير", market: "٨٫٠٠ – ١٢٫٠٠ ر.ع.", prepaid: "٥٫٥٠ ر.ع.", save: "٣١٪" },
      ],
      footer: "الشحنات التي تتجاوز ٢٠ كجم أو ذات الأحجام الاستثنائية تُسعَّر عند الطلب. تشمل جميع الأسعار توصيل الميل الأخير في مسقط مع إثبات التسليم الرقمي.",
    },
    billing: {
      prepaidEyebrow: "حساب مدفوع مسبقًا",
      prepaidTitle: "متاح لجميع الخطط منذ اليوم الأول",
      prepaidItems: [
        "ادفع حزمتك الشهرية مقدمًا — تُحمَّل الأرصدة فورًا",
        "استخدم التوصيلات في أي وقت خلال الشهر",
        "أعد الشحن بأسعار الحزمة إذا احتجت المزيد",
        "الأرصدة غير المستخدمة تُرحَّل حتى ٣٠ يومًا (حد أقصى ٢٥٪ من الحزمة)",
        "وصول كامل للوحة: رفع الشحنات والتتبع ومراقبة الإنفاق لحظيًا",
      ],
      postpaidEyebrow: "حساب آجل",
      postpaidTitle: "متاح للعملاء ذوي الحجم العالي عند التقديم",
      postpaidItems: [
        "حد أدنى ٢٠٠ توصيلة شهريًا للتأهل",
        "دورة فواتير كل أسبوعين — فواتير كل ١٤ يومًا",
        "فاتورة تفصيلية لكل توصيلة حسب فئة الوزن والوجهة",
        "نفس أسعار الحزم — لا رسوم إضافية على الآجل",
        "الموافقة الائتمانية تتطلب اتفاقية موقعة وتجربة مدفوعة لأسبوعين",
      ],
    },
    ctaTitle: "ابدأ تجربتك التجريبية المجانية لأسبوعين",
    ctaSubtitle: "دون التزام. دون مخاطرة. وصول كامل لمنصة Twasal Awasal — لوحة تحكم وتتبع مباشر وإثبات تسليم رقمي وتقارير — منذ اليوم الأول.",
  },
  about: {
    eyebrow: "عن Twasal Awasal",
    titleLead: "عن",
    titleHighlight: "Twasal Awasal.",
    description: "Twasal Awasal هي مزود خدمات توصيل وبريد عُماني مرخّص، مبني على أساس التوجيه بالذكاء الاصطناعي، والتتبع اللحظي، وإثبات التسليم الرقمي. ننقل الطرود والبضائع والشحن عبر مسقط ودول الخليج وما بعدها — للأفراد الذين يرسلون طردًا واحدًا وللأعمال التي تشحن بكميات كبيرة.",
    tagline: "شبكة عُمان للتوصيل المدعومة بالذكاء الاصطناعي.",
    missionTitle: "مهمتنا",
    missionBody: "تقديم تجربة توصيل سريعة وشفافة وموثوقة لكل فرد وشركة في عُمان — مدعومة بالتكنولوجيا، ومنفذة على يد فريق يعرف المنطقة.",
    visionTitle: "رؤيتنا",
    visionBody: "سلطنة يتم فيها تتبع كل طرد — محلي أو إقليمي أو دولي — والتحقق منه وتسليمه بثقة كاملة، وحيث تكون Twasal Awasal الاسم الأول الذي يثق به العُمانيون وشركات الخليج.",
    valuesEyebrow: "قِيمنا",
    valuesTitle: "ثلاثة مبادئ لن نتنازل عنها.",
    valuesDescription: "تظهر في كل شحنة نخطط لها، وكل مسار نديره، وكل عميل نخدمه.",
    values: [
      { title: "الموثوقية أولًا", description: "نُقيّم أنفسنا على أساس التسليم في الوقت المحدد ورضا العملاء — لا على عدد الخدمات التي نعرضها." },
      { title: "تكنولوجيا بهدف واضح", description: "الذكاء الاصطناعي والأتمتة موجودان لجعل كل توصيل أسرع وأكثر شفافية — لا كميزة لأجل الميزة." },
      { title: "مبنية لعُمان، جاهزة للمنطقة", description: "مرخّصة ومتوافقة محليًا، مع الشبكة والتكنولوجيا للنمو عبر دول الخليج." },
    ],
    journeyEyebrow: "رحلتنا",
    journeyTitle: "بُنيت بهدف. تنمو مع المنطقة.",
    journeyDescription: "",
    milestones: [
      { year: "٢٠٢٣", title: "تأسست في مسقط", text: "أُسست Twasal Awasal لتقديم خدمات توصيل حديثة مدعومة بالتكنولوجيا للأفراد والأعمال في عُمان." },
      { year: "٢٠٢٤", title: "إطلاق المنصة الرقمية", text: "أُطلق التتبع المباشر عبر GPS وإثبات التسليم الرقمي والحجز عبر الإنترنت للعملاء في مسقط." },
      { year: "٢٠٢٥", title: "التوسع الإقليمي", text: "أُطلقت خدمات الشحن عبر الحدود في دول الخليج، إلى جانب أول عقود المؤسسات." },
      { year: "٢٠٢٦", title: "إطلاق التوجيه بالذكاء الاصطناعي", text: "تخطيط المسارات المدعوم بالذكاء الاصطناعي وتنبؤ أوقات الوصول متاح الآن على مستوى الشبكة، لتقليل أوقات التسليم عبر جميع خطوط الخدمة." },
    ],
    teamEyebrow: "الشبكة",
    teamTitleLead: "مرخّصة في عُمان.",
    teamTitleHighlight: "جاهزة للمنطقة.",
    teamBody1: "Twasal Awasal هي مزود خدمات توصيل وبريد مرخّص بالكامل، مقره في مسقط، مع امتداد عبر الحدود في دول الخليج.",
    teamBody2: "كل مسار محسّن بالذكاء الاصطناعي. كل توصيلة متتبعة. كل شحنة جاهزة للامتثال.",
    teamCards: [
      { stat: "مسقط", sub: "المقر الرئيسي" },
      { stat: "دول الخليج", sub: "الامتداد الإقليمي" },
      { stat: "بالذكاء الاصطناعي", sub: "كل مسار" },
      { stat: "مرخّصة", sub: "متوافقة بالكامل" },
    ],
    ctaTitle: "جرّب طريقة الشحن الأذكى.",
    ctaSubtitle: "سواء كنت ترسل طردًا واحدًا أو تدير لوجستيات مؤسسية، Twasal Awasal جاهزة.",
  },
  partner: {
    eyebrow: "تواصل معنا",
    titleLead: "دعنا نتولى",
    titleHighlight: "توصيلك.",
    titleTrail: "",
    description: "سواء كنت ترسل طردًا شخصيًا أو تُنشئ حسابًا للأعمال، فريقنا مستعد لمساعدتك.",
    contactEyebrow: "تحدث مع فريقنا",
    contactTitle: "نحن هنا لمساعدتك.",
    contactBody: "أرسل لنا رسالة سريعة — سيرد فريقنا خلال يوم عمل واحد. للشحنات العاجلة، يُرجى استخدام صفحة التتبع أو مراسلة hello@twasalawasal.com مباشرة.",
    headquarters: "المقر الرئيسي",
    hqLocation: "مسقط، سلطنة عُمان",
    partnershipsLabel: "الاستفسارات العامة",
    businessLabel: "استفسارات الأعمال",
    pilotEyebrow: "تحتاج إلى تتبع شحنة؟",
    pilotBody: "هل لديك رقم تتبع بالفعل؟ تخطَّ النموذج — توجه مباشرة إلى صفحة تتبع الشحنة للحصول على الحالة اللحظية.",
    form: {
      name: "الاسم الكامل",
      namePh: "اسمك",
      email: "البريد الإلكتروني",
      emailPh: "you@example.com",
      company: "الشركة (اختياري)",
      companyPh: "اسم شركتك",
      phone: "الهاتف (اختياري)",
      phonePh: "+968 …",
      plan: "نوع الاستفسار",
      planPh: "اختر نوع الاستفسار",
      volume: "حجم التوصيل الشهري (للأعمال فقط)",
      volumePh: "اختر الحجم",
      message: "أخبرنا عن استفسارك",
      messagePh: "ما ترسله وإلى أين، وأي تفاصيل أخرى...",
      consent: "بإرسال هذا النموذج، فإنك توافق على أن يتم التواصل معك من قِبَل Twasal Awasal بشأن استفسارك.",
    },
    planOptions: [
      "إرسال طرد",
      "حساب للأعمال",
      "تتبع شحنة موجودة",
      "استفسار آخر",
    ],
    volumeOptions: [
      "أقل من ١٠٠ توصيلة شهريًا",
      "١٠٠ – ٥٠٠ توصيلة شهريًا",
      "٥٠٠ – ٢٬٠٠٠ توصيلة شهريًا",
      "أكثر من ٢٬٠٠٠ توصيلة شهريًا",
    ],
    success: {
      title: "تم استلام استفسارك.",
      body: "شكرًا لتواصلك. سيتواصل معك فريقنا خلال يوم عمل واحد.",
    },
    errorFallback: "حدث خطأ ما. يُرجى المحاولة مرة أخرى، أو مراسلتنا مباشرة على hello@twasalawasal.com.",
  },
  track: {
    eyebrow: "تتبع الشحنة",
    title: "تتبع شحنتك",
    subtitle: "أدخل رقم التتبع أدناه للحصول على الحالة اللحظية — من الاستلام حتى التسليم.",
    placeholder: "أدخل رقم التتبع",
    button: "تتبع الآن",
    statusSteps: [
      "تم استلام الطلب",
      "تم الاستلام",
      "في الطريق",
      "خارج للتسليم",
      "تم التسليم",
    ],
    supportTitle: "لا تجد شحنتك؟",
    supportBody: "تحقق من رقم التتبع، أو تواصل مع فريق الدعم — يسعدنا مساعدتك.",
    supportCta: "تواصل مع الدعم",
    notFoundMessage: "لم نتمكن من العثور على شحنة مطابقة لرقم التتبع هذا. يُرجى التحقق من الرقم والمحاولة مرة أخرى، أو التواصل مع الدعم للمساعدة.",
    yourShipment: "شحنتك",
    estimatedDelivery: "التسليم المتوقع",
    lastUpdate: "آخر تحديث",
    fromTo: "المسار",
  },
  login: {
    eyebrow: "الدخول إلى لوحة التحكم",
    title: "لوحة التحكم قريبًا.",
    body: "بوابة عملاء Twasal Awasal تُطلَق على مراحل. هل أنت مسجل بالفعل؟ سيشاركك مدير حسابك بيانات الدخول قريبًا.",
    note: "الموجة القادمة من الوصول: تدريجية — حسب تاريخ التفعيل",
  },
  footer: {
    tagline: "شبكة عُمان للتوصيل المدعومة بالذكاء الاصطناعي — تنقل كل طرد وشحنة وبضاعة، بذكاء أكبر.",
    platform: "روابط سريعة",
    company: "الشركة",
    contact: "تواصل معنا",
    links: {
      features: "الخدمات",
      solutions: "الحلول للأعمال",
      pricing: "الأسعار",
      track: "تتبع الشحنة",
      login: "الدخول",
      about: "عن الشركة",
      partner: "تواصل معنا",
      careers: "الوظائف",
    },
    location: "مسقط، سلطنة عُمان",
    copyright: "© {year} Twasal Awasal. جميع الحقوق محفوظة. مسقط، سلطنة عُمان.",
    motto: "منصة واحدة. كل توصيل. رؤية كاملة.",
    socialLabel: "تابعنا",
  },
};

const translations = { en, ar };
export default translations;
